// combatpro/panels/monsters/studio/useMonsterStudioEditor.js
//
// Combat PRO - Monster Studio editor runtime (Stage 56: Editor Runtime Foundation).
//
// The editor runtime for Monster Studio, expressed as a React hook. It OWNS the
// editor behaviour and state - the working monster model, the active tab, and the
// dirty flag - and exposes clean methods the React components consume. React
// renders; this runtime owns the editor logic. It is NOT CombatContext (no
// context ownership change) and performs no persistence, cloud, save, or DOM work.
//
// All model creation, mapping, validation, cloning, CSV, display-name, and
// save-status logic is delegated to the pure Stage 55 engine
// (monsterStudioEngine.js). This runtime never recreates that logic; it only
// orchestrates immutable state updates and the legacy edit side effects (dirty +
// status bumps).
//
// Faithful to the legacy runtime (battletracker.html):
//   - setField        <- viStudioField (11036): nested dotted-path write; bumps
//                        status to 'modified' when the model is official-sourced
//                        AND its status is 'unmodified' OR 'official'.
//   - setCsv          <- viStudioSetCsv (11445): engine.parseCsv into the field;
//                        bumps status only when status is 'unmodified'.
//   - addCard         <- viStudioAddCard (11144): appends a blank card (legendary
//                        defaults cost 1); sets dirty; does NOT bump status.
//   - removeCard      <- viStudioRemoveCard (11145): splices; dirty; no status bump.
//   - updateCard      <- viStudioCardField (11146): writes one card field; dirty;
//                        bumps status only when status is 'unmodified'.
//   - addSpell        <- viStudioAddSpell (11162): trims, de-duplicates, appends;
//                        dirty. (Input clearing is the picker's concern.)
//   - removeSpell     <- viStudioRemoveSpell (11169): splices; dirty.
//   - setActiveTab    <- viStudioSetTab (11019): changes the active editor tab.
//   - newMonster      <- viStudioNew: resets to a fresh blank model on the
//                        identity tab and clears dirty.
//
// NOTE (legacy inconsistency, preserved): viStudioField bumps status on
// 'unmodified' OR 'official', while viStudioSetCsv and viStudioCardField bump only
// on 'unmodified'. This runtime preserves each function's exact condition rather
// than normalising them.
//
// No DOM, no querySelector, no innerHTML, no fetch, no localStorage, no backend.
// No optional chaining. No em dash.

import { useState } from 'react';
import {
  createStudioMonster,
  studioDisplayName,
  deriveSaveStatus,
  validateStudioMonster,
  parseCsv
} from '../../../monster/monsterStudioEngine';

// Immutable nested write along a dotted path (the React-safe form of the legacy
// in-place traversal in viStudioField). Clones only the objects on the path.
function setNested(model, path, value) {
  var parts = path.split('.');
  var next = Object.assign({}, model);
  var cursor = next;
  for (var i = 0; i < parts.length - 1; i++) {
    var key = parts[i];
    var child = (cursor[key] == null) ? {} : Object.assign({}, cursor[key]);
    cursor[key] = child;
    cursor = child;
  }
  cursor[parts[parts.length - 1]] = value;
  return next;
}

function useMonsterStudioEditor() {
  const [model, setModelState] = useState(function() { return createStudioMonster(); });
  const [activeTab, setActiveTab] = useState('identity');
  const [dirty, setDirty] = useState(false);

  // Derived values, recomputed each render from the engine (no duplication).
  const displayName = studioDisplayName(model);
  const saveStatus = deriveSaveStatus(model);
  const validation = validateStudioMonster(model);

  function setModel(next) {
    setModelState(next);
  }

  // Stage 57: load an existing model into the editor and clear the dirty flag,
  // the faithful equivalent of the legacy load tail (viStudioPick / viStudioAfterLoad,
  // battletracker.html 11211-11226): viStudio is replaced and viStudioDirty set to
  // false. The active tab is intentionally preserved (legacy viStudioAfterLoad does
  // not change viStudioTab). The Library runtime calls this; the editor stays the
  // owner of the model.
  function loadModel(next) {
    setModelState(next);
    setDirty(false);
  }

  // Stage 58: load a model into the editor and mark it unsaved (dirty), the seam for
  // persistence operations that produce an in-progress model the user must still
  // save - Duplicate, Save As pre-clone, Favourite/Archive toggles, and version
  // restore/duplicate (legacy viStudioCloneCurrent / viStudioDuplicate set
  // viStudioDirty=true). dirty tracking stays owned by the editor; persistence asks
  // through this seam rather than setting dirty itself. Tab is preserved.
  function setModelDirty(next) {
    setModelState(next);
    setDirty(true);
  }

  function newMonster() {
    setModelState(createStudioMonster());
    setActiveTab('identity');
    setDirty(false);
  }

  function setField(path, value) {
    setModelState(function(prev) {
      var next = setNested(prev, path, value);
      if (next.sourceMonster && (next.status === 'unmodified' || next.status === 'official')) {
        next.status = 'modified';
      }
      return next;
    });
    setDirty(true);
  }

  function setCsv(path, value) {
    setModelState(function(prev) {
      var next = Object.assign({}, prev);
      next[path] = parseCsv(value);
      if (next.sourceMonster && next.status === 'unmodified') {
        next.status = 'modified';
      }
      return next;
    });
    setDirty(true);
  }

  function addCard(listName) {
    setModelState(function(prev) {
      var next = Object.assign({}, prev);
      var arr = Array.isArray(prev[listName]) ? prev[listName].slice() : [];
      arr.push({ name: '', desc: '', cost: (listName === 'legendaryActions' ? 1 : undefined) });
      next[listName] = arr;
      return next;
    });
    setDirty(true);
  }

  function removeCard(listName, idx) {
    setModelState(function(prev) {
      var next = Object.assign({}, prev);
      var arr = Array.isArray(prev[listName]) ? prev[listName].slice() : [];
      arr.splice(idx, 1);
      next[listName] = arr;
      return next;
    });
    setDirty(true);
  }

  function updateCard(listName, idx, key, value) {
    setModelState(function(prev) {
      if (!Array.isArray(prev[listName]) || !prev[listName][idx]) { return prev; }
      var next = Object.assign({}, prev);
      var arr = prev[listName].slice();
      arr[idx] = Object.assign({}, arr[idx]);
      arr[idx][key] = value;
      next[listName] = arr;
      if (next.sourceMonster && next.status === 'unmodified') {
        next.status = 'modified';
      }
      return next;
    });
    setDirty(true);
  }

  function addSpell(name) {
    var v = String(name == null ? '' : name).trim();
    if (!v) { return; }
    setModelState(function(prev) {
      var next = Object.assign({}, prev);
      var arr = Array.isArray(prev.spells) ? prev.spells.slice() : [];
      if (arr.indexOf(v) === -1) { arr.push(v); }
      next.spells = arr;
      return next;
    });
    setDirty(true);
  }

  function removeSpell(idx) {
    setModelState(function(prev) {
      var next = Object.assign({}, prev);
      var arr = Array.isArray(prev.spells) ? prev.spells.slice() : [];
      arr.splice(idx, 1);
      next.spells = arr;
      return next;
    });
    setDirty(true);
  }

  return {
    model: model,
    activeTab: activeTab,
    dirty: dirty,
    displayName: displayName,
    saveStatus: saveStatus,
    validation: validation,
    setActiveTab: setActiveTab,
    setField: setField,
    setCsv: setCsv,
    addCard: addCard,
    removeCard: removeCard,
    updateCard: updateCard,
    addSpell: addSpell,
    removeSpell: removeSpell,
    newMonster: newMonster,
    setModel: setModel,
    loadModel: loadModel,
    setModelDirty: setModelDirty
  };
}

export default useMonsterStudioEditor;
